import { GraphQLQuery } from '@aws-amplify/api';
import { notifyError } from "../../../../components/ServiceNotifications";
import {
    Customer,
    CreateCustomerInput,
    CreateCustomerResponse,
    UpdateCustomerInput,
    UpdateCustomerResponse,
    GetCustomerResponse,
    ProvisionBillingCustomerForCurrentAccountResponse
} from '../types';
import { getCustomerQuery } from './queries';
import {
    createCustomerMutation,
    updateCustomerMutation,
    provisionBillingCustomerForCurrentAccountMutation,
} from './mutations';
import { billingClient } from '../../billing-api-integration/client';

export type { Customer, CreateCustomerInput, UpdateCustomerInput } from '../types';

export const createCustomer = async (input: CreateCustomerInput): Promise<Customer | undefined> => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<CreateCustomerResponse>>({
            query: createCustomerMutation,
            variables: { input },
        });
        
        console.log('Customer created:', response.data?.createCustomer);
        return response.data?.createCustomer;
    } catch (e: unknown) {
        console.error('Error creating customer:', e);
        console.error('Customer creation payload:', { ...input, status: 'Active' });
        const errors = (e as { errors?: unknown })?.errors;
        if (errors) {
            console.error('GraphQL errors:', errors);
        }
        notifyError(e);
    }
};

export const fetchCustomer = async (id: string): Promise<Customer | undefined> => {
    try {
        const response = await billingClient.graphql<GraphQLQuery<GetCustomerResponse>>({
            query: getCustomerQuery,
            variables: { id },
        });
        
        return response.data?.getCustomer;
    } catch (e: unknown) {
        console.error('Error fetching customer:', e);
        console.error('Customer fetch ID:', id);
        const errors = (e as { errors?: unknown })?.errors;
        if (errors) {
            console.error('GraphQL errors:', errors);
        }
        notifyError(e);
    }
};

export const updateCustomer = async (id: string, input: Partial<UpdateCustomerInput>): Promise<Customer | undefined> => {
    try {
        const updateInput: UpdateCustomerInput = {
            id,
            ...input
        };
        
        const response = await billingClient.graphql<GraphQLQuery<UpdateCustomerResponse>>({
            query: updateCustomerMutation,
            variables: { input: updateInput },
        });
        
        console.log('Customer updated:', response.data?.updateCustomer);
        return response.data?.updateCustomer;
    } catch (e: unknown) {
        console.error('Error updating customer:', e);
        console.error('Customer update payload:', { id, ...input });
        const errors = (e as { errors?: unknown })?.errors;
        if (errors) {
            console.error('GraphQL errors:', errors);
        }
        notifyError(e);
    }
};

type CognitoUserAttributes = {
    given_name?: string;
    family_name?: string;
    email?: string;
};

// FNP-717: provision (or return) the billing Customer for the CURRENT account and
// link it, entirely server-side. billing-api derives the account from the caller's
// token, so there is no input and the browser never writes the account->customer
// linkage (FNP-490 single-writer). Unlike createCustomer above (which swallows via
// notifyError), this THROWS on failure so the caller can distinguish success from a
// failed setup and surface the real error. Idempotent + safe to retry.
export const provisionBillingCustomerForCurrentAccount = async (): Promise<Customer> => {
    const response = await billingClient.graphql<GraphQLQuery<ProvisionBillingCustomerForCurrentAccountResponse>>({
        query: provisionBillingCustomerForCurrentAccountMutation,
    });

    const customer = response.data?.provisionBillingCustomerForCurrentAccount;
    if (!customer?.id) {
        throw new Error('Failed to provision billing profile.');
    }
    return customer;
};

export const createCustomerFromUser = async (user: CognitoUserAttributes): Promise<Customer | undefined> => {
    const customerPayload: CreateCustomerInput = {
        name: `${user.given_name || ''} ${user.family_name || ''}`.trim(),
        status: 'ACTIVE',  // Must be uppercase per GraphQL schema
        contactInfo: {
            email: user.email,
        },
    };

    return await createCustomer(customerPayload);
};


